import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import * as FullStory from '@fullstory/browser';
import { capitalize, chain, startCase } from 'lodash';
import type { IUserInfoResponse, IUpdateUser } from '@/api-v2/web/user';
import { getUserInfo, updateUserInfo } from '@/api-v2/web/user';
import type { PdfParametersResponseDTO } from '@/api-v2/web/teams';
import { getPdfParameters } from '@/api-v2/web/teams';
import store from '@/store';
import getEnv from '@/utils/getEnv';
import { getIsPdf } from '@/utils/getIsPdf';

const { isProduction } = getEnv();

export interface IUserState {
  user: IUserInfoResponse | null;
}

export interface IJwtPayload {
  exp: number;
  userId: number;
}

function getLegacyEnv() {
  if (import.meta.env.VITE_STAGE === 'prod') return 'production';

  if (import.meta.env.VITE_STAGE === 'uat') return 'staging';

  if (import.meta.env.VITE_STAGE === 'test') return 'test';

  return 'development';
}

@Module({ dynamic: true, store, name: 'User' })
class User extends VuexModule implements IUserState {
  public user: IUserInfoResponse | null = null;
  public pdfParameters: PdfParametersResponseDTO | null = null;

  @Mutation
  private SET_USER(user: IUserInfoResponse): void {
    this.user = user;
  }

  @Mutation
  private SET_PDF_PARAMETERS(pdfParameters: PdfParametersResponseDTO): void {
    this.pdfParameters = pdfParameters;
  }

  @Action({ rawError: true })
  public async GetUserInfo(): Promise<void> {
    const signal = new AbortController().signal;

    // Load the PDF parameters only if we are on a PDF page on application startup. Should be sufficient for
    // chromeless for now.
    if (getIsPdf(window.location.href)) {
      const [user, pdfParameters] = await Promise.all([getUserInfo(signal), getPdfParameters(signal)]);
      this.SET_USER(user);
      this.SET_PDF_PARAMETERS(pdfParameters);
      return;
    }

    const user = await getUserInfo(signal);
    this.SET_USER(user);
  }

  @Action({ rawError: true })
  public async UpdateUserInfo(userInfo: IUpdateUser): Promise<void> {
    const user = await updateUserInfo(userInfo);
    this.SET_USER(user);
  }

  @Action({ rawError: true })
  public async IdentifyFullstory(): Promise<void> {
    if (!isProduction || import.meta.env.VITE_CLIENT !== 'Premialab') {
      return;
    }

    FullStory.init({ orgId: 'H93Q7' });
    if (!this.user) return;

    // Back-compat with legacy environmental variable to avoid breaking FullStory
    const legacyEnv = getLegacyEnv();

    let uid = `${legacyEnv}-${this.user.id}`;
    if (this.user.impersonator) {
      uid = `${legacyEnv}-${this.user.impersonator.id}-as-${this.user.id}`;
    }

    FullStory.identify(uid, {
      email: this.user.impersonator ? undefined : this.user.email.toLowerCase(),
      displayName: this.user.impersonator
        ? `${this.user.impersonator.email.toLowerCase()} as ${this.user.email.toLowerCase()}`
        : undefined,
      env: legacyEnv,
      client: import.meta.env.VITE_CLIENT,
      createdAt: this.user.createdAt.split(' ').join('T'),
      lastName: chain(this.user.lastname).split(' ').capitalize().join(' ').value(),
      firstName: chain(this.user.firstname).split(' ').capitalize().join(' ').value(),
      name: `${this.user.firstname} ${this.user.lastname}`,
      phone: this.user.telephone || undefined,
      country: this.user.country || undefined,
      title: capitalize(startCase(this.user.title)) || undefined,
      company: startCase(this.user.company) || undefined,
      industry: startCase(this.user.sector) || undefined,
      // Custom traits
      'User Phone': this.user.telephone || undefined,
      'User Country Code': this.user.country || undefined,
      'User Title': startCase(this.user.title) || undefined,
      'User Company Name': startCase(this.user.company) || undefined,
      'User Company Industry': startCase(this.user.sector) || undefined,
      impersonator: this.user.impersonator ? this.user.impersonator.email.toLowerCase() : undefined,
      impersonatorId: this.user.impersonator ? this.user.impersonator.id : undefined,
      impersonating: this.user.impersonator ? this.user.email.toLowerCase() : undefined,
      impersonatingId: this.user.impersonator ? this.user.id : undefined,
    });
  }

  @Action({ rawError: true })
  public async InitPdfParameters(): Promise<void> {
    if (this.pdfParameters) return;

    const signal = new AbortController().signal;

    const pdfParameters = await getPdfParameters(signal);
    this.SET_PDF_PARAMETERS(pdfParameters);
  }
}

export const UserModule = getModule(User);
